import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    InputAdornment,
    Chip,
    IconButton,
    Tooltip,
    Grid,
    FormControlLabel,
    Switch,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Restore as RestoreIcon,
    Android as AndroidIcon,
    Apple as AppleIcon,
    Language as WebIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import { useUserList, useDeleteUser, useRestoreUser } from '../../hooks/useUser';
import { AuthProvider, DeviceType, SubscriptionType, UserDeletionMode, UserRole } from '../../types/user';
import { formatKstDate } from '../../utils/dateTime';
import UserDeleteDialog from './UserDeleteDialog';

export default function UserList() {
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState<UserRole | ''>('');
    const [providerFilter, setProviderFilter] = useState<AuthProvider | ''>('');
    const [subscriptionFilter, setSubscriptionFilter] = useState<SubscriptionType | ''>('');
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
    const [restoreSuccessMessage, setRestoreSuccessMessage] = useState<string | null>(null);

    const { data, isLoading, error } = useUserList({
        page: page + 1,
        size,
        searchKeyword: search || undefined,
        role: roleFilter || undefined,
        provider: providerFilter || undefined,
        subscriptionType: subscriptionFilter || undefined,
        includeDeleted,
        sortBy: 'createdAt',
        sortDirection: 'desc',
    });

    const deleteUser = useDeleteUser();
    const restoreUser = useRestoreUser();

    const triggerSearch = () => {
        setSearch(searchInput.trim());
        setPage(0);
    };

    const handleDeleteConfirm = (mode: UserDeletionMode) => {
        if (deleteTargetId == null) return;
        deleteUser.mutate(
            {
                id: deleteTargetId,
                data: { deletionMode: mode },
            },
            {
                onSuccess: () => setDeleteTargetId(null),
            }
        );
    };

    const handleRestore = (id: number) => {
        if (confirm('이 사용자를 복원하시겠습니까? 원본 정보(이메일, Firebase UID 등)로 복구됩니다.')) {
            restoreUser.mutate(id, {
                onSuccess: () => {
                    setRestoreSuccessMessage(`사용자 #${id}를 복원했습니다.`);
                },
            });
        }
    };

    const getRoleChip = (role: UserRole) => {
        switch (role) {
            case UserRole.SUPER_ADMIN:
                return <Chip label="슈퍼관리자" color="error" size="small" />;
            case UserRole.ADMIN:
                return <Chip label="관리자" color="warning" size="small" />;
            default:
                return <Chip label="사용자" color="default" size="small" />;
        }
    };

    const getProviderChip = (provider: AuthProvider) => {
        switch (provider) {
            case AuthProvider.GOOGLE:
                return <Chip label="Google" color="primary" size="small" variant="outlined" />;
            case AuthProvider.APPLE:
                return <Chip label="Apple" color="info" size="small" variant="outlined" />;
            case AuthProvider.EMAIL:
                return <Chip label="Email" color="secondary" size="small" variant="outlined" />;
            case AuthProvider.KAKAO:
                return <Chip label="Kakao" color="warning" size="small" variant="outlined" />;
            default:
                return <Chip label="Unknown" color="default" size="small" variant="outlined" />;
        }
    };

    const getDeviceIcon = (deviceType?: DeviceType | null) => {
        switch (deviceType) {
            case DeviceType.ANDROID:
                return (
                    <Tooltip title="Android">
                        <AndroidIcon fontSize="small" sx={{ color: '#3DDC84' }} />
                    </Tooltip>
                );
            case DeviceType.IOS:
                return (
                    <Tooltip title="iOS">
                        <AppleIcon fontSize="small" sx={{ color: '#555' }} />
                    </Tooltip>
                );
            case DeviceType.WEB:
                return (
                    <Tooltip title="Web">
                        <WebIcon fontSize="small" sx={{ color: '#1976d2' }} />
                    </Tooltip>
                );
            default:
                return <Typography variant="caption" color="text.disabled">-</Typography>;
        }
    };

    const renderCountry = (countryCode?: string | null, timezone?: string | null) => {
        const code = countryCode?.toUpperCase();
        if (!code || code.length !== 2) {
            return (
                <Tooltip title={timezone || '국가 미식별'}>
                    <Typography variant="caption" color="text.disabled">-</Typography>
                </Tooltip>
            );
        }
        const flag = String.fromCodePoint(
            ...[...code].map((c) => 0x1f1e6 - 65 + c.charCodeAt(0)),
        );
        const tooltip = timezone ? `${code} · ${timezone}` : code;
        return (
            <Tooltip title={tooltip}>
                <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>
                    {flag}
                </Box>
            </Tooltip>
        );
    };

    const getSubscriptionChip = (type: SubscriptionType, isActive: boolean) => {
        if (!isActive || type === SubscriptionType.FREE) {
            return <Chip label="무료" color="default" size="small" />;
        }
        const labels: Record<SubscriptionType, string> = {
            [SubscriptionType.FREE]: '무료',
            [SubscriptionType.MONTHLY]: '월간',
            [SubscriptionType.YEARLY]: '연간',
            [SubscriptionType.LIFETIME]: '평생',
        };
        return <Chip label={labels[type]} color="success" size="small" />;
    };

    const users = data?.content ?? [];

    return (
        <Box p={3}>
            <Typography variant="h4" sx={{ mb: 3 }}>
                사용자 관리
            </Typography>

            {restoreSuccessMessage && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRestoreSuccessMessage(null)}>
                    {restoreSuccessMessage}
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error.message || '데이터를 불러오는데 실패했습니다.'}
                </Alert>
            )}

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                        fullWidth
                        size="small"
                        label="검색 (이메일, 닉네임)"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                triggerSearch();
                            }
                        }}
                        placeholder="이메일/닉네임 검색 후 Enter"
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={triggerSearch}
                                            edge="end"
                                        >
                                            <SearchIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 2 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>역할</InputLabel>
                        <Select
                            value={roleFilter}
                            label="역할"
                            onChange={(e) => {
                                setRoleFilter(e.target.value as UserRole | '');
                                setPage(0);
                            }}
                        >
                            <MenuItem value="">전체</MenuItem>
                            <MenuItem value={UserRole.SUPER_ADMIN}>슈퍼관리자</MenuItem>
                            <MenuItem value={UserRole.ADMIN}>관리자</MenuItem>
                            <MenuItem value={UserRole.USER}>사용자</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 2 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>가입방식</InputLabel>
                        <Select
                            value={providerFilter}
                            label="가입방식"
                            onChange={(e) => {
                                setProviderFilter(e.target.value as AuthProvider | '');
                                setPage(0);
                            }}
                        >
                            <MenuItem value="">전체</MenuItem>
                            <MenuItem value={AuthProvider.GOOGLE}>Google</MenuItem>
                            <MenuItem value={AuthProvider.APPLE}>Apple</MenuItem>
                            <MenuItem value={AuthProvider.EMAIL}>Email</MenuItem>
                            <MenuItem value={AuthProvider.KAKAO}>Kakao</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 2 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>구독</InputLabel>
                        <Select
                            value={subscriptionFilter}
                            label="구독"
                            onChange={(e) => {
                                setSubscriptionFilter(e.target.value as SubscriptionType | '');
                                setPage(0);
                            }}
                        >
                            <MenuItem value="">전체</MenuItem>
                            <MenuItem value={SubscriptionType.FREE}>무료</MenuItem>
                            <MenuItem value={SubscriptionType.MONTHLY}>월간</MenuItem>
                            <MenuItem value={SubscriptionType.YEARLY}>연간</MenuItem>
                            <MenuItem value={SubscriptionType.LIFETIME}>평생</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={includeDeleted}
                                onChange={(e) => {
                                    setIncludeDeleted(e.target.checked);
                                    setPage(0);
                                }}
                            />
                        }
                        label="삭제된 사용자 포함"
                    />
                </Grid>
            </Grid>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>이메일</TableCell>
                            <TableCell>닉네임</TableCell>
                            <TableCell>역할</TableCell>
                            <TableCell>가입방식</TableCell>
                            <TableCell align="center">기기</TableCell>
                            <TableCell align="center">국가</TableCell>
                            <TableCell>구독</TableCell>
                            <TableCell align="right">깃털</TableCell>
                            <TableCell align="right">AI 크레딧</TableCell>
                            <TableCell>마지막 로그인</TableCell>
                            <TableCell>가입일</TableCell>
                            <TableCell align="center">관리</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={13} align="center">
                                    로딩 중...
                                </TableCell>
                            </TableRow>
                        ) : users.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={13} align="center">
                                    데이터가 없습니다.
                                </TableCell>
                            </TableRow>
                        ) : (
                            users.map((user) => (
                                <TableRow
                                    key={user.id}
                                    hover
                                    onClick={() => navigate(`/user/${user.id}`)}
                                    sx={{
                                        cursor: 'pointer',
                                        backgroundColor: user.deletedAt ? 'rgba(255, 0, 0, 0.05)' : undefined,
                                    }}
                                >
                                    <TableCell>{user.id}</TableCell>
                                    <TableCell>
                                        {user.email}
                                        {user.deletedAt && (
                                            <Chip label="삭제됨" color="error" size="small" sx={{ ml: 1 }} />
                                        )}
                                    </TableCell>
                                    <TableCell>{user.nickname || '-'}</TableCell>
                                    <TableCell>{getRoleChip(user.role)}</TableCell>
                                    <TableCell>{getProviderChip(user.provider)}</TableCell>
                                    <TableCell align="center">{getDeviceIcon(user.deviceType)}</TableCell>
                                    <TableCell align="center">{renderCountry(user.countryCode, user.timezone)}</TableCell>
                                    <TableCell>
                                        {getSubscriptionChip(user.subscriptionType, user.isSubscriptionActive)}
                                    </TableCell>
                                    <TableCell align="right">{user.totalXp.toLocaleString()}</TableCell>
                                    <TableCell align="right">
                                        {user.aiCreditBalance ? (
                                            <Tooltip
                                                title={`이번 주 지급 ${user.aiCreditBalance.grantedAmount.toLocaleString()} · ${user.aiCreditBalance.isSubscribed ? '구독' : '무료'}`}
                                            >
                                                <span>{user.aiCreditBalance.balance.toLocaleString()}</span>
                                            </Tooltip>
                                        ) : (
                                            <Typography variant="caption" color="text.disabled">-</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {formatKstDate(user.lastLoginAt)}
                                    </TableCell>
                                    <TableCell>
                                        {formatKstDate(user.createdAt)}
                                    </TableCell>
                                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                        {user.deletedAt ? (
                                            <Tooltip title="복원">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleRestore(user.id)}
                                                >
                                                    <RestoreIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title="삭제">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => setDeleteTargetId(user.id)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={data?.totalCount ?? 0}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={size}
                onRowsPerPageChange={(e) => {
                    setSize(parseInt(e.target.value, 10));
                    setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50, 100]}
                labelRowsPerPage="페이지당 행:"
            />

            <UserDeleteDialog
                open={deleteTargetId != null}
                loading={deleteUser.isPending}
                userId={deleteTargetId}
                onClose={() => setDeleteTargetId(null)}
                onConfirm={handleDeleteConfirm}
            />
        </Box>
    );
}
